package com.isaias.fyattravel.web.pages.explore

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLImageElement

private data class HeroPhoto(val src: String, val alt: String)

private val HERO_PHOTOS = listOf(
    HeroPhoto("images/explore/hero/hero-1.jpg", "Murchison Falls and the Nile gorge, seen from a wide rainbow-lit viewpoint"),
    HeroPhoto("images/explore/hero/hero-2.jpg", "Elephants crossing the savanna beneath palm trees in Uganda"),
    HeroPhoto("images/explore/hero/hero-3.jpg", "A lion resting in the shade of a tree in Queen Elizabeth National Park"),
    HeroPhoto("images/explore/hero/hero-4.jpg", "A dirt road winding along the shore of Lake Albert, Uganda"),
    HeroPhoto("images/explore/hero/hero-5.jpg", "A trekker photographing the trail through Bwindi Impenetrable Forest"),
    HeroPhoto("images/explore/hero/hero-6.jpg", "Close-up of a mountain gorilla in Bwindi Impenetrable Forest"),
)

private const val HERO_INTERVAL_MS = 10_000

private fun prefersReducedMotion(): Boolean =
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * Full-bleed hero background that cycles through [HERO_PHOTOS] every 10s,
 * dimming briefly between swaps (same technique as the flights-page hero
 * collage in home/DynamicHero.kt). Sits absolutely behind the hero content;
 * caller is responsible for `position: relative` on the hero container.
 */
@Composable
fun ExploreHeroBackground() {
    var index by remember { mutableStateOf(0) }
    var dim by remember { mutableStateOf(false) }
    val reducedMotion = remember { prefersReducedMotion() }

    if (!reducedMotion) {
        DisposableEffect(Unit) {
            val intervalId = window.setInterval({
                dim = true
                window.setTimeout({
                    index = (index + 1) % HERO_PHOTOS.size
                    dim = false
                }, 400)
            }, HERO_INTERVAL_MS)
            onDispose { window.clearInterval(intervalId) }
        }

        // Warm the browser cache with the next photo so the swap paints instantly.
        LaunchedEffect(index) {
            val next = HERO_PHOTOS[(index + 1) % HERO_PHOTOS.size]
            (document.createElement("img") as HTMLImageElement).src = next.src
        }
    }

    val current = HERO_PHOTOS[index]

    Div({
        classes("explore-hero-bg")
        attr("role", "img")
        attr("aria-label", current.alt)
        style {
            property("background-image", "url(${current.src})")
            opacity(if (dim) 0.55 else 1.0)
        }
    })
}
